import styled from 'styled-components'

/**
 * A small "Guide" link beside a product page's title, to its how-to on docs.cadinu.io (Bez, 2026-10-04). One look everywhere: a book and the word,
 * quiet until hovered, a new tab. It never moves anything: a fixed 28 px pill that does not shrink, with a 44 px touch area drawn outside it.
 * Slugs: cadinu-docs `docs-site-20261004`, site/ sidebar from SUMMARY.md (Starlight, no trailing slash).
 */
export const DOCS_URL = 'https://docs.cadinu.io'

export const GUIDES = {
  swap: { path: 'guides/how-to-swap', title: 'How to swap' },
  farm: { path: 'guides/how-to-farm', title: 'How to farm' },
  stake: { path: 'guides/how-to-stake', title: 'How to stake' },
  competition: { path: 'guides/create-trading-competition', title: 'Create a trading competition' },
  lock: { path: 'guides/lock-tokens', title: 'Lock tokens or liquidity' },
  profile: { path: 'guides/profile-and-referral-link', title: 'Profile and referral link' },
  tokenInfo: { path: 'guides/token-project-info', title: 'Verify token info (blue and gold ticks)' },
  // clickToWin (products/click-to-win-c2w) left on 2026-10-08 with the page (archive/click-to-win-20261008)
  bnbFees: { path: 'guides/bnb-for-network-fees', title: 'Getting BNB for network fees' },
} as const
export type GuideKey = keyof typeof GUIDES

export const guideHref = (key: GuideKey) => `${DOCS_URL}/${GUIDES[key].path}`

const Pill = styled.a`
  position: relative;
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 11px 0 9px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: ${({ theme }) => (theme.isDark ? 'rgba(127, 212, 232, 0.06)' : 'rgba(31, 199, 212, 0.06)')};
  color: ${({ theme }) => theme.colors.textSubtle};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.01em;
  white-space: nowrap;
  text-decoration: none;
  vertical-align: middle;
  transition: color 120ms ease, border-color 120ms ease;
  /* a 44 px tall touch area without a 44 px tall pill */
  &::after {
    content: '';
    position: absolute;
    inset: -8px -4px;
  }
  svg {
    flex: none;
  }
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/** A title and its guide on one line, the guide wrapping under the title when the line is full (never out of the page). */
export const GuideRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 12px;
  row-gap: 6px;
  min-width: 0;
  > * {
    min-width: 0;
  }
`

function Book() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  )
}

export default function GuideLink({ guide, style }: { guide: GuideKey; style?: React.CSSProperties }) {
  const g = GUIDES[guide]
  return (
    <Pill
      href={guideHref(guide)}
      target="_blank"
      rel="noopener noreferrer"
      title={`${g.title}: guide on docs.cadinu.io`}
      aria-label={`Guide: ${g.title} (docs.cadinu.io, opens in a new tab)`}
      data-testid="guide-link"
      data-guide={guide}
      style={style}
    >
      <Book />
      Guide
    </Pill>
  )
}
